import { Champ, ChampSelection } from "@/composants/ui/champ";
import type { CommandeClientProposee, LigneCommandeFournisseur } from "@/lib/api";

/**
 * Les champs d'une ligne de commande fournisseur, saisis à la création de la
 * commande — elle naît avec ses premières lignes (ADR-0044) — comme sur sa fiche.
 * Chaque ligne sert sa commande client, et un lot de celle-ci ou aucun (ticket
 * #37) : on choisit la commande client, puis le lot parmi les siens. Les
 * commandes client proposées sont celles du même propriétaire, sur lesquelles
 * on agit ; la base refuse les autres.
 */

const SANS_LOT = "";

export function valeursDeLigne(commandeClientId: string, ligne?: LigneCommandeFournisseur) {
  return {
    commande_client_id: ligne?.commande_client_id ?? commandeClientId,
    lot_id: ligne?.lot_id ?? SANS_LOT,
    designation: ligne?.designation ?? "",
    reference: ligne?.reference ?? "",
    quantite_commandee: ligne ? String(ligne.quantite_commandee) : "",
    unite: ligne?.unite ?? "U",
    prix_unitaire_ht: ligne?.prix_unitaire_ht != null ? String(ligne.prix_unitaire_ht) : "",
  };
}

export type ValeursLigne = ReturnType<typeof valeursDeLigne>;

/**
 * Ce que l'API reçoit. Sans la permission de voir les montants, le prix n'est ni
 * montré ni touché.
 */
export function champsDeLigne(valeurs: ValeursLigne, avecPrix: boolean): Record<string, unknown> {
  const champs: Record<string, unknown> = {
    commande_client_id: valeurs.commande_client_id,
    lot_id: valeurs.lot_id || null,
    designation: valeurs.designation.trim(),
    reference: valeurs.reference.trim() || null,
    quantite_commandee: valeurs.quantite_commandee.replace(",", "."),
    unite: valeurs.unite.trim() || "U",
  };
  if (avecPrix) {
    champs.prix_unitaire_ht = valeurs.prix_unitaire_ht.replace(",", ".") || null;
  }
  return champs;
}

export function ChampsLigne({
  valeurs,
  onChange,
  commandes,
  avecPrix,
}: {
  valeurs: ValeursLigne;
  onChange: (valeurs: ValeursLigne) => void;
  commandes: CommandeClientProposee[];
  avecPrix: boolean;
}) {
  const poser = (champ: keyof ValeursLigne) => (valeur: string) =>
    onChange({ ...valeurs, [champ]: valeur });
  const servie = commandes.find((commande) => commande.id === valeurs.commande_client_id);

  return (
    <div className="grid gap-3 sm:grid-cols-2">
      <Champ
        libelle="Désignation"
        required
        value={valeurs.designation}
        onChange={(e) => poser("designation")(e.target.value)}
      />
      <ChampSelection
        libelle="Commande client"
        required
        options={commandes.map((commande) => ({
          valeur: commande.id,
          libelle: commande.numero_commande_client,
        }))}
        value={valeurs.commande_client_id}
        // Un lot appartient à sa commande client : en changer repart sans lot.
        onChange={(e) =>
          onChange({ ...valeurs, commande_client_id: e.target.value, lot_id: SANS_LOT })
        }
      />
      <ChampSelection
        libelle="Lot destinataire"
        options={[
          { valeur: SANS_LOT, libelle: "Aucun lot (article générique)" },
          ...(servie?.lots ?? []).map((lot) => ({
            valeur: lot.id,
            libelle: `${lot.code_lot} — ${lot.intitule}`,
          })),
        ]}
        value={valeurs.lot_id}
        onChange={(e) => poser("lot_id")(e.target.value)}
      />
      <Champ
        libelle="Référence"
        value={valeurs.reference}
        onChange={(e) => poser("reference")(e.target.value)}
      />
      <div className="grid grid-cols-2 gap-3">
        <Champ
          libelle="Quantité"
          required
          inputMode="decimal"
          value={valeurs.quantite_commandee}
          onChange={(e) => poser("quantite_commandee")(e.target.value)}
        />
        <Champ
          libelle="Unité"
          required
          value={valeurs.unite}
          onChange={(e) => poser("unite")(e.target.value)}
        />
      </div>
      {avecPrix ? (
        <Champ
          libelle="Prix unitaire HT (€)"
          inputMode="decimal"
          value={valeurs.prix_unitaire_ht}
          onChange={(e) => poser("prix_unitaire_ht")(e.target.value)}
        />
      ) : null}
    </div>
  );
}
